import React, { useState } from "react";
import PageShell from "../dashboard/PageShell";
import { Search, FileDown, Calendar, Pencil, Trash2 } from "lucide-react";

interface ProgressReportItem {
  id: string;
  stt: number;
  function: string;
  topic: string;
  monthlyReport: string;
  reportContent: string;
  nextMonthPlan: string;
  sentDate: string;
}

const mockData: ProgressReportItem[] = [
  {
    id: "1",
    stt: 1,
    function: "Nghiên cứu viên",
    topic: "Nghiên cứu ứng dụng AI trong quản lý tài nguyên nước",
    monthlyReport: "Báo cáo tháng 9/2025",
    reportContent:
      "Hoàn thành 80% nghiên cứu thuật toán AI, đã thử nghiệm trên dữ liệu mẫu",
    nextMonthPlan:
      "Tiếp tục tối ưu hóa thuật toán và chuẩn bị báo cáo kỹ thuật",
    sentDate: "15/09/2025",
  },
  {
    id: "2",
    stt: 2,
    function: "Chủ nhiệm đề tài",
    topic: "Phát triển hệ thống cảnh báo lũ sớm",
    monthlyReport: "Báo cáo tháng 9/2025",
    reportContent: "Đã lắp đặt 5 trạm đo mưa, thu thập dữ liệu trong 3 tháng",
    nextMonthPlan: "Phân tích dữ liệu và xây dựng mô hình dự báo",
    sentDate: "20/09/2025",
  },
  {
    id: "3",
    stt: 3,
    function: "Thành viên",
    topic:
      "Nghiên cứu xác định nguyên nhân và đề xuất giải pháp khoa học công nghệ nhằm ổn định các cửa sông và vùng bờ biển tỉnh Bình Thuận",
    monthlyReport: "Báo cáo tháng 9/2025",
    reportContent: "Khảo sát thực địa tại 3 cửa sông, thu thập mẫu đất và nước",
    nextMonthPlan: "Phân tích mẫu trong phòng thí nghiệm và đánh giá tác động",
    sentDate: "25/09/2025",
  },
];

const ProgressReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [responsible, setResponsible] = useState("");
  const [startDate, setStartDate] = useState("2025-09-01");
  const [endDate, setEndDate] = useState("2025-09-22");
  // removed: keyword field per user request
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] =
    useState<ProgressReportItem | null>(null);

  return (
    <PageShell title="Báo cáo tiến độ">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Báo cáo tiến độ
          </h1>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm báo cáo tiến độ"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4 items-center">
              <select
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              >
                <option>- Phụ trách -</option>
                <option value="nghien-cuu-vien">Nghiên cứu viên</option>
                <option value="chu-nhiem">Chủ nhiệm đề tài</option>
                <option value="thanh-vien">Thành viên</option>
              </select>

              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
              />

              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
              />

              {/* removed: keyword input per user request */}
            </div>

            {/* Row 3 - Action buttons */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Xác nhận xóa
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Bạn có chắc chắn muốn xóa báo cáo này không? Hành động này
                    không thể hoàn tác.
                  </p>
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setReportToDelete(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={() => {
                        // TODO: integrate delete API using reportToDelete?.id
                        setShowDeleteConfirm(false);
                        setReportToDelete(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Xóa
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-64">
                    Đề tài
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Báo cáo tháng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-64">
                    Nội dung báo cáo
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-64">
                    Kế hoạch tháng tiếp theo
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Ngày gửi
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-16">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-64">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight underline">
                        {item.topic} »
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-48">
                      {item.monthlyReport}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-64">
                      <div className="leading-tight text-sm">
                        {item.reportContent}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-64">
                      <div className="leading-tight text-sm">
                        {item.nextMonthPlan}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 w-32 text-center">
                      {item.sentDate}
                    </td>
                    <td className="px-2 py-4 text-sm font-medium text-gray-900 w-24">
                      <div className="flex items-center space-x-2">
                        <button className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setReportToDelete(item);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">3</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ProgressReport;
// Delete confirmation modal appended below component for clarity
